import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { 
  MapPin, Clock, Euro, Search, Plane, ChevronDown, Star, ArrowRight, 
  Users, Calendar, Filter, SortAsc, Award, CheckCircle, Phone, 
  MessageCircle, Loader2, X
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CustomCalendar from '../components/CustomCalendar';
import CustomTimePicker from '../components/CustomTimePicker';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Region colors for badges
const REGION_COLORS = {
  'Messinia': 'bg-emerald-100 text-emerald-700',
  'Laconia': 'bg-purple-100 text-purple-700',
  'Arcadia': 'bg-blue-100 text-blue-700',
  'Argolida': 'bg-amber-100 text-amber-700',
  'Corinthia': 'bg-rose-100 text-rose-700',
  'Achaia': 'bg-cyan-100 text-cyan-700',
  'Ilia': 'bg-orange-100 text-orange-700',
};

// Sort options
const SORT_OPTIONS = [
  { id: 'alphabetical', label: 'A to Z' },
  { id: 'distance', label: 'Distance' },
  { id: 'region', label: 'Region' },
];

const RoutesPage = () => {
  const navigate = useNavigate();
  
  // Data state
  const [routes, setRoutes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('from'); // 'from' or 'to'
  
  // Filter/Sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [sortBy, setSortBy] = useState('distance');
  const [showRegionDropdown, setShowRegionDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  // Booking form state
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');
  const [passengers, setPassengers] = useState(2);
  const [pickupDate, setPickupDate] = useState(null);
  const [pickupTime, setPickupTime] = useState('10:00');
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [showPassengerDropdown, setShowPassengerDropdown] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  // Fetch data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const endpoint = activeTab === 'from' 
          ? `${API_BASE}/routes/from/kalamata-airport`
          : `${API_BASE}/routes/to/kalamata-airport`;
        
        const [routesRes, locationsRes] = await Promise.all([
          fetch(endpoint),
          fetch(`${API_BASE}/locations`)
        ]);
        
        if (routesRes.ok) {
          const routesData = await routesRes.json();
          setRoutes(routesData);
        }
        
        if (locationsRes.ok) {
          const locationsData = await locationsRes.json();
          setLocations(locationsData);
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setPickupDate(tomorrow);

    // Close dropdowns on outside click
    const handleClick = () => {
      setShowFromDropdown(false);
      setShowToDropdown(false);
      setShowPassengerDropdown(false);
      setShowCalendar(false);
      setShowTimePicker(false);
      setShowRegionDropdown(false);
      setShowSortDropdown(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [activeTab]);

  // Get unique regions from locations
  const regions = useMemo(() => {
    const uniqueRegions = [...new Set(locations.map(l => l.region).filter(Boolean))];
    return uniqueRegions.sort();
  }, [locations]);

  // Sort locations: Kalamata Airport first, then alphabetically
  const sortLocations = (locs) => {
    return [...locs].sort((a, b) => {
      if (a.slug === 'kalamata-airport') return -1;
      if (b.slug === 'kalamata-airport') return 1;
      return a.name.localeCompare(b.name);
    });
  };

  // Filter locations for booking form dropdowns (name or region)
  const filteredFromLocations = sortLocations(locations.filter(loc => 
    loc.name.toLowerCase().includes(fromSearch.toLowerCase()) ||
    loc.region?.toLowerCase().includes(fromSearch.toLowerCase())
  ));
  const filteredToLocations = sortLocations(locations.filter(loc => 
    loc.name !== fromLocation && (
      loc.name.toLowerCase().includes(toSearch.toLowerCase()) ||
      loc.region?.toLowerCase().includes(toSearch.toLowerCase())
    )
  ));

  // Parse numeric values from route data
  const parseDistance = (dist) => {
    if (!dist) return 0;
    const match = dist.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const parseDuration = (dur) => {
    if (!dur) return 0;
    // Handle formats like "1h 30min", "45 min", "2h"
    let minutes = 0;
    const hourMatch = dur.match(/(\d+)\s*h/);
    const minMatch = dur.match(/(\d+)\s*min/);
    if (hourMatch) minutes += parseInt(hourMatch[1]) * 60;
    if (minMatch) minutes += parseInt(minMatch[1]);
    return minutes;
  };

  // Get location region
  const getLocationRegion = (slug) => {
    const loc = locations.find(l => l.slug === slug);
    return loc?.region || 'Unknown';
  };

  // Get the relevant slug based on direction (non-airport location)
  const getRouteLocationSlug = (route) => {
    return activeTab === 'from' ? route.to_slug : route.from_slug;
  };

  // Get the relevant name based on direction
  const getRouteLocationName = (route) => {
    return activeTab === 'from' ? route.to_name : route.from_name;
  };

  // Filter and sort routes
  const filteredRoutes = useMemo(() => {
    let filtered = [...routes];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r => {
        const locationName = getRouteLocationName(r);
        const locationSlug = getRouteLocationSlug(r);
        return locationName.toLowerCase().includes(term) ||
          getLocationRegion(locationSlug).toLowerCase().includes(term);
      });
    }
    
    // Apply region filter
    if (selectedRegion !== 'all') {
      filtered = filtered.filter(r => getLocationRegion(getRouteLocationSlug(r)) === selectedRegion);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'alphabetical':
          return getRouteLocationName(a).localeCompare(getRouteLocationName(b));
        case 'distance':
          return parseDistance(a.distance) - parseDistance(b.distance);
        case 'region':
          const regionA = getLocationRegion(getRouteLocationSlug(a));
          const regionB = getLocationRegion(getRouteLocationSlug(b));
          return regionA.localeCompare(regionB) || getRouteLocationName(a).localeCompare(getRouteLocationName(b));
        default:
          return parseDistance(a.distance) - parseDistance(b.distance);
      }
    });
    
    return filtered;
  }, [routes, searchTerm, selectedRegion, sortBy, locations, activeTab]);

  // Group routes by region (for region sort display)
  const routesByRegion = useMemo(() => {
    const grouped = {};
    filteredRoutes.forEach(route => {
      const region = getLocationRegion(getRouteLocationSlug(route));
      if (!grouped[region]) grouped[region] = [];
      grouped[region].push(route);
    });
    return grouped;
  }, [filteredRoutes, locations, activeTab]);

  // Format date/time for display
  const formatDateDisplay = (date) => {
    if (!date) return 'Select date';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTimeDisplay = (time) => {
    if (!time) return 'Select time';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const formatDateForUrl = (date) => {
    if (!date) return '';
    // Use local date components to avoid timezone shift issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle booking search
  const handleSearch = () => {
    if (fromLocation && toLocation) {
      const fromSlug = locations.find(l => l.name === fromLocation)?.slug;
      const toSlug = locations.find(l => l.name === toLocation)?.slug;
      if (fromSlug && toSlug) {
        navigate(`/routes/${fromSlug}/${toSlug}`);
      }
    }
  };

  // Get min price for display
  const getMinPrice = (route) => {
    if (route.min_price) return route.min_price;
    const prices = (route.vehicle_prices || []).map(vp => vp.price).filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : route.base_price || 0;
  };

  // Set document meta tags (works around React 19 + react-helmet-async issues)
  useDocumentMeta({
    title: 'Transfer Routes from Kalamata Airport | All Destinations | Kalamata Airport Taxi',
    description: 'Browse all transfer routes from Kalamata Airport to destinations across the Peloponnese. Fixed prices to Messinia, Laconia, Arcadia, Argolida, and more.',
    canonical: 'https://kalamataairporttaxi.com/routes',
    ogTitle: 'Transfer Routes from Kalamata Airport | All Destinations',
    ogDescription: 'Browse all transfer routes from Kalamata Airport to destinations across the Peloponnese. Fixed prices to Messinia, Laconia, Arcadia, Argolida, and more.',
    ogUrl: 'https://kalamataairporttaxi.com/routes',
    twitterTitle: 'Transfer Routes from Kalamata Airport | All Destinations',
    twitterDescription: 'Browse all transfer routes from Kalamata Airport to destinations across the Peloponnese.'
  });

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Header />
      
      {/* Hero Section with Booking Form */}
      <section className="relative pt-32 pb-16">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/b57d6e5e-ecd0-42dc-a0f5-2e671a531a1b/images/f71b10f14811f014f96387558dfa51f019c3fe8ac5385033db0f57aee44a0408.png')` }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#2d232e]/95 via-[#2d232e]/85 to-[#2d232e]/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2d232e]/50 via-transparent to-[#2d232e]/30" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#36827f]/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#36827f]/5 rounded-full blur-3xl pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
            <span className="text-[#36827f]">Routes</span>
          </nav>

          <div className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-5">
              <Award className="w-4 h-4 text-[#36827f]" />
              <span className="text-sm font-medium text-white/90">90+ Destinations</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Find Your <span className="text-[#36827f]">Transfer Route</span>
            </h1>
            
            <p className="text-lg text-white/70 max-w-xl">
              Explore all our routes from Kalamata Airport to destinations across the Peloponnese. Fixed prices, professional drivers, 24/7 service.
            </p>
          </div>

          {/* Booking Form - Simplified */}
          <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-4 max-w-3xl relative z-[100]">
            <div className="flex flex-col lg:flex-row items-stretch">
              {/* From */}
              <div className="relative flex-[1.5] min-w-0" onClick={(e) => e.stopPropagation()}>
                <div 
                  className="flex items-center gap-3 px-5 py-5 hover:bg-gray-50 transition rounded-xl h-full cursor-pointer"
                  onClick={() => { setShowFromDropdown(true); setShowToDropdown(false); }}
                >
                  <MapPin className="w-6 h-6 text-[#36827f] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">From</p>
                    <input
                      type="text"
                      placeholder="Select pickup location"
                      value={showFromDropdown ? fromSearch : fromLocation}
                      onChange={(e) => { setFromSearch(e.target.value); setShowFromDropdown(true); }}
                      onFocus={() => { setShowFromDropdown(true); setFromSearch(''); }}
                      className={`bg-transparent outline-none w-full truncate placeholder-gray-400 ${fromLocation && !showFromDropdown ? 'font-medium text-gray-900' : 'text-gray-900'}`}
                    />
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${showFromDropdown ? 'rotate-180' : ''}`} />
                </div>
                {showFromDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-[200] max-h-[280px] overflow-y-auto">
                    {filteredFromLocations.length > 0 ? filteredFromLocations.map(loc => (
                      <div 
                        key={loc.slug}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition"
                        onClick={() => { setFromLocation(loc.name); setFromSearch(''); setShowFromDropdown(false); }}
                      >
                        <p className="font-medium text-gray-900">{loc.name}</p>
                        <p className="text-sm text-gray-500">{loc.region}</p>
                      </div>
                    )) : (
                      <div className="px-4 py-3 text-gray-500 text-sm">No locations found</div>
                    )}
                  </div>
                )}
              </div>

              <div className="hidden lg:block w-px bg-gray-200 my-3" />

              {/* To */}
              <div className="relative flex-[1.5] min-w-0" onClick={(e) => e.stopPropagation()}>
                <div 
                  className="flex items-center gap-3 px-5 py-5 hover:bg-gray-50 transition rounded-xl h-full cursor-pointer"
                  onClick={() => { setShowToDropdown(true); setShowFromDropdown(false); }}
                >
                  <MapPin className="w-6 h-6 text-[#36827f] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">To</p>
                    <input
                      type="text"
                      placeholder="Select destination"
                      value={showToDropdown ? toSearch : toLocation}
                      onChange={(e) => { setToSearch(e.target.value); setShowToDropdown(true); }}
                      onFocus={() => { setShowToDropdown(true); setToSearch(''); }}
                      className={`bg-transparent outline-none w-full truncate placeholder-gray-400 ${toLocation && !showToDropdown ? 'font-medium text-gray-900' : 'text-gray-900'}`}
                    />
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${showToDropdown ? 'rotate-180' : ''}`} />
                </div>
                {showToDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-[200] max-h-[280px] overflow-y-auto">
                    {filteredToLocations.length > 0 ? filteredToLocations.map(loc => (
                      <div 
                        key={loc.slug}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition"
                        onClick={() => { setToLocation(loc.name); setToSearch(''); setShowToDropdown(false); }}
                      >
                        <p className="font-medium text-gray-900">{loc.name}</p>
                        <p className="text-sm text-gray-500">{loc.region}</p>
                      </div>
                    )) : (
                      <div className="px-4 py-3 text-gray-500 text-sm">No locations found</div>
                    )}
                  </div>
                )}
              </div>

              {/* Search Button */}
              <button 
                onClick={handleSearch}
                disabled={!fromLocation || !toLocation}
                className="m-3 px-10 py-5 rounded-xl font-bold text-white text-lg transition-all bg-[#36827f] hover:bg-[#2d6f6c] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#36827f]/20 hover:shadow-xl"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Routes Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tabs */}
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => setActiveTab('from')}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'from' 
                  ? 'bg-[#36827f] text-white shadow-lg shadow-[#36827f]/20' 
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <Plane className="w-5 h-5" />
              From Kalamata Airport
            </button>
            <button
              onClick={() => setActiveTab('to')}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'to' 
                  ? 'bg-[#36827f] text-white shadow-lg shadow-[#36827f]/20' 
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <Plane className="w-5 h-5 rotate-180" />
              To Kalamata Airport
            </button>
          </div>

          {/* Filters Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search destinations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition-colors"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Region Filter */}
              <div className="relative w-full sm:w-auto" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => { setShowRegionDropdown(!showRegionDropdown); setShowSortDropdown(false); }}
                  className="flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition w-full sm:min-w-[180px]"
                >
                  <Filter className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-700 font-medium">
                    {selectedRegion === 'all' ? 'All Regions' : selectedRegion}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 ml-auto transition-transform ${showRegionDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showRegionDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
                    <div
                      className={`px-4 py-3 cursor-pointer transition-colors ${selectedRegion === 'all' ? 'bg-[#36827f]/10 text-[#36827f] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                      onClick={() => { setSelectedRegion('all'); setShowRegionDropdown(false); }}
                    >
                      All Regions
                    </div>
                    {regions.map(region => (
                      <div
                        key={region}
                        className={`px-4 py-3 cursor-pointer transition-colors ${selectedRegion === region ? 'bg-[#36827f]/10 text-[#36827f] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                        onClick={() => { setSelectedRegion(region); setShowRegionDropdown(false); }}
                      >
                        {region}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sort */}
              <div className="relative w-full sm:w-auto" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => { setShowSortDropdown(!showSortDropdown); setShowRegionDropdown(false); }}
                  className="flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition w-full sm:min-w-[180px]"
                >
                  <SortAsc className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-700 font-medium">
                    {SORT_OPTIONS.find(s => s.id === sortBy)?.label}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 ml-auto transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showSortDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
                    {SORT_OPTIONS.map(option => (
                      <div
                        key={option.id}
                        className={`px-4 py-3 cursor-pointer transition-colors ${sortBy === option.id ? 'bg-[#36827f]/10 text-[#36827f] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                        onClick={() => { setSortBy(option.id); setShowSortDropdown(false); }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600">
              <span className="font-semibold text-gray-900">{filteredRoutes.length}</span> routes found
              {selectedRegion !== 'all' && <span className="text-[#36827f]"> in {selectedRegion}</span>}
              {searchTerm && <span className="text-[#36827f]"> matching "{searchTerm}"</span>}
            </p>
            {(searchTerm || selectedRegion !== 'all') && (
              <button
                onClick={() => { setSearchTerm(''); setSelectedRegion('all'); }}
                className="text-sm text-[#36827f] hover:underline font-medium"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Route Cards */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#36827f]" />
              <span className="ml-2 text-gray-500">Loading routes...</span>
            </div>
          ) : filteredRoutes.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No routes found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </div>
          ) : sortBy === 'region' ? (
            // Grouped by region display
            <div className="space-y-10">
              {Object.entries(routesByRegion).sort(([a], [b]) => a.localeCompare(b)).map(([region, regionRoutes]) => (
                <div key={region}>
                  <div className="flex items-center gap-3 mb-4">
                    <h2 className="text-xl font-bold text-gray-900">{region}</h2>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${REGION_COLORS[region] || 'bg-gray-100 text-gray-700'}`}>
                      {regionRoutes.length} destinations
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {regionRoutes.map(route => (
                      <RouteCard 
                        key={route.route_id} 
                        route={route} 
                        region={region}
                        getMinPrice={getMinPrice}
                        direction={activeTab}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Regular grid display
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredRoutes.map(route => (
                <RouteCard 
                  key={route.route_id} 
                  route={route} 
                  region={getLocationRegion(getRouteLocationSlug(route))}
                  getMinPrice={getMinPrice}
                  direction={activeTab}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#2d232e]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Can't find your destination?
          </h2>
          <p className="text-white/70 mb-8 text-lg">
            We cover all destinations in the Peloponnese. Contact us for a custom quote.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a 
              href="tel:+306932421170"
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#36827f] text-white font-semibold hover:bg-[#2d6f6c] transition"
            >
              <Phone className="w-5 h-5" />
              Call Us
            </a>
            <a 
              href="https://wa.me/306932421170"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#25D366] text-white font-semibold hover:bg-[#20bd5a] transition"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Route Card Component
const RouteCard = ({ route, region, getMinPrice, direction = 'from' }) => {
  const price = getMinPrice(route);
  const regionColor = REGION_COLORS[region] || 'bg-gray-100 text-gray-700';
  
  // Determine display based on direction
  const isFromAirport = direction === 'from';
  const pickupLocation = isFromAirport ? 'Kalamata Airport' : (route.from_name || route.to_name);
  const dropLocation = isFromAirport ? route.to_name : 'Kalamata Airport';
  
  return (
    <Link
      to={`/routes/${route.from_slug}/${route.to_slug}`}
      className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-[#36827f]/30 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
      data-testid={`route-card-${route.to_slug}`}
    >
      <div className="p-5">
        {/* Region badge on top */}
        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium mb-4 ${regionColor}`}>
          {region}
        </span>
        
        {/* Route direction with vertical line */}
        <div className="flex gap-4 mb-4">
          {/* Vertical line with dots */}
          <div className="flex flex-col items-center py-1">
            <div className="w-3 h-3 rounded-full bg-[#36827f]"></div>
            <div className="w-0.5 flex-1 bg-[#36827f]/30 my-1"></div>
            <div className="w-3 h-3 rounded-full bg-[#36827f]/40"></div>
          </div>
          
          {/* From/To text */}
          <div className="flex flex-col justify-between flex-1 min-w-0">
            <div className="pb-2">
              <span className="text-gray-400 text-sm">From </span>
              <span className="font-semibold text-gray-900">{pickupLocation}</span>
            </div>
            <div className="pt-2">
              <span className="text-gray-400 text-sm">To </span>
              <span className="font-semibold text-gray-900">{dropLocation}</span>
            </div>
          </div>
        </div>

        {/* Stats and Price */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <MapPin className="h-4 w-4" />
              <span>{route.distance}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              <span>{route.duration}</span>
            </div>
          </div>
          <div className="text-right">
            <span className="text-xs text-gray-400">from </span>
            <span className="text-xl font-bold text-[#36827f]">€{price}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default RoutesPage;
